import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { Skeleton } from 'primereact/skeleton';
import { Tooltip } from 'primereact/tooltip';
import { useCallback, useMemo, useState } from 'react';
import { endpointPortfolioCorrelationClusterGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import type { CorrelationClusterData } from '~/types/visualization';
import { openPopoutWindow } from '~/utils/popoutUtils';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { CorrelationClusterChart } from './CorrelationClusterChart';

export interface CorrelationMetrics {
  avgCorrelation: number | null;
  maxCorrelation: number | null;
  minCorrelation: number | null;
}

export interface TopCorrelationPair {
  asset1: string;
  asset2: string;
  correlation: number;
  isPositive: boolean;
}

export type CorrelationClusterWidgetProps = {
  data: CorrelationClusterData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
  lookbackDays?: number;
  correlationThreshold?: number;
  initialPortfolioType?: 'current' | 'proposed';
  initialMetricType?: 'drawdown' | 'beta';
  portfolioId?: string | null;
  portfolioName?: string | null;
  correlationMetrics?: CorrelationMetrics;
  topCorrelatedPairs?: TopCorrelationPair[];
  correlationTickerNames?: Map<string, string>;
};

export const CorrelationClusterWidget: React.FC<
  CorrelationClusterWidgetProps
> = ({
  data: initialData,
  isLoading: initialLoading,
  error: initialError,
  refetch: initialRefetch,
  handleRemoveWidget,
  widgetId,
  title = 'Correlation Cluster Analysis',
  snapshotId,
  lookbackDays = 252,
  correlationThreshold = 0.85,
  initialPortfolioType = 'proposed',
  initialMetricType = 'drawdown',
  portfolioId,
  portfolioName,
  correlationMetrics,
  topCorrelatedPairs = [],
  correlationTickerNames,
}) => {
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);

  // Portfolio type toggle state (local to each widget instance)
  const [portfolioType, setPortfolioType] = useState<'current' | 'proposed'>(
    initialPortfolioType,
  );

  // Metric type toggle state (local to each widget instance)
  const [metricType, setMetricType] = useState<'drawdown' | 'beta'>(
    initialMetricType,
  );

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Fetch data with portfolio type (only when portfolio type changes from default)
  const {
    data: portfolioTypeData,
    isLoading: portfolioTypeLoading,
    error: portfolioTypeError,
    refetch: portfolioTypeRefetch,
  } = useQuery<CorrelationClusterData>({
    queryKey: [
      'correlation-cluster',
      snapshotId,
      lookbackDays,
      correlationThreshold,
      portfolioType,
    ],
    queryFn: async () => {
      if (!snapshotId) {
        throw new Error('No snapshot ID available');
      }

      const response = await endpointPortfolioCorrelationClusterGET(
        snapshotId,
        {
          lookbackDays,
          correlationThreshold,
          portfolioType,
        },
      );

      return response;
    },
    enabled: !!snapshotId && portfolioType !== 'proposed',
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Use portfolio-specific data if available, otherwise use initial data
  const data = portfolioType === 'proposed' ? initialData : portfolioTypeData;
  const isLoading =
    portfolioType === 'proposed' ? initialLoading : portfolioTypeLoading;
  const error =
    portfolioType === 'proposed' ? initialError : portfolioTypeError;
  const refetch =
    portfolioType === 'proposed' ? initialRefetch : portfolioTypeRefetch;

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  const handlePopout = useCallback(() => {
    openPopoutWindow({
      widgetType: 'correlation-cluster',
      snapshotId: snapshotId || undefined,
      title: title || 'Correlation Cluster Analysis',
      lookbackDays,
      correlationThreshold,
      portfolioType,
      metricType,
      portfolioId: portfolioId || undefined,
      portfolioName: portfolioName || undefined,
    });
  }, [
    snapshotId,
    title,
    lookbackDays,
    correlationThreshold,
    portfolioType,
    metricType,
    portfolioId,
    portfolioName,
  ]);

  const noData = useMemo(() => {
    return !data || !data.nodes || data.nodes.length === 0;
  }, [data]);

  // Find best matching company based on search query
  const selectedTicker = useMemo(() => {
    if (!searchQuery || !data?.nodes) return null;

    const query = searchQuery.toLowerCase().trim();
    if (!query) return null;

    // Find exact ticker match first
    const exactTickerMatch = data.nodes.find(
      (node) => node.ticker.toLowerCase() === query,
    );
    if (exactTickerMatch) return exactTickerMatch.ticker;

    // Find ticker that starts with query
    const tickerStartMatch = data.nodes.find((node) =>
      node.ticker.toLowerCase().startsWith(query),
    );
    if (tickerStartMatch) return tickerStartMatch.ticker;

    // Find company name that starts with query
    const companyStartMatch = data.nodes.find((node) =>
      node.company_name.toLowerCase().startsWith(query),
    );
    if (companyStartMatch) return companyStartMatch.ticker;

    // Find ticker that contains query
    const tickerContainsMatch = data.nodes.find((node) =>
      node.ticker.toLowerCase().includes(query),
    );
    if (tickerContainsMatch) return tickerContainsMatch.ticker;

    // Find company name that contains query
    const companyContainsMatch = data.nodes.find((node) =>
      node.company_name.toLowerCase().includes(query),
    );
    if (companyContainsMatch) return companyContainsMatch.ticker;

    return null;
  }, [searchQuery, data]);

  // Calculate min/max beta from node data
  const { minBeta, maxBeta } = useMemo(() => {
    if (!data?.nodes) return { minBeta: null, maxBeta: null };

    const validBetas = data.nodes
      .map((n) => n.beta)
      .filter((b) => b != null && !isNaN(Number(b)) && isFinite(Number(b)))
      .map((b) => Number(b));

    if (validBetas.length === 0) return { minBeta: null, maxBeta: null };

    return {
      minBeta: Math.min(...validBetas),
      maxBeta: Math.max(...validBetas),
    };
  }, [data]);

  // Use correlation metrics from props (fetched from portfolio stats)
  const correlationStats = correlationMetrics || {
    avgCorrelation: null,
    maxCorrelation: null,
    minCorrelation: null,
  };

  // Build a set of tickers with non-zero allocation from current data
  const activeTickers = useMemo(() => {
    const set = new Set<string>();
    if (data?.nodes) {
      data.nodes.forEach((node) => {
        if (node.allocation_percent > 0) {
          set.add(node.ticker);
          set.add(node.ticker.toUpperCase());
          const baseTicker = node.ticker.split('.')[0];
          if (baseTicker) {
            set.add(baseTicker);
            set.add(baseTicker.toUpperCase());
          }
        }
      });
    }
    return set;
  }, [data]);

  // Filter top correlations to only include pairs where both assets are in current portfolio
  const filteredTopCorrelatedPairs = useMemo(() => {
    if (activeTickers.size === 0) return topCorrelatedPairs;
    return topCorrelatedPairs.filter((pair) => {
      const asset1Match =
        activeTickers.has(pair.asset1) ||
        activeTickers.has(pair.asset1.toUpperCase()) ||
        activeTickers.has(pair.asset1.split('.')[0]);
      const asset2Match =
        activeTickers.has(pair.asset2) ||
        activeTickers.has(pair.asset2.toUpperCase()) ||
        activeTickers.has(pair.asset2.split('.')[0]);
      return asset1Match && asset2Match;
    });
  }, [topCorrelatedPairs, activeTickers]);

  // Create ticker to company name map from nodes data
  const tickerToCompanyName = useMemo(() => {
    const map = new Map<string, string>();
    if (data?.nodes) {
      data.nodes.forEach((node) => {
        if (node.ticker && node.company_name) {
          // Store with original ticker
          map.set(node.ticker, node.company_name);
          // Also store uppercase version
          map.set(node.ticker.toUpperCase(), node.company_name);
          // Store base ticker (before any suffix like .US)
          const baseTicker = node.ticker.split('.')[0];
          if (baseTicker && !map.has(baseTicker)) {
            map.set(baseTicker, node.company_name);
            map.set(baseTicker.toUpperCase(), node.company_name);
          }
        }
      });
    }
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No correlation cluster data available."
          subTitle="Run portfolio analysis to generate correlation cluster data"
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        widgetId={widgetId}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="p-3 h-full">
          <Card
            className="h-full"
            pt={{
              content: {
                className: 'p-0 m-0 h-full flex flex-column',
              },
              body: {
                className: 'h-full',
              },
            }}
          >
            {/* Control Panel - Search, Buttons, Stats, and Legend */}
            <div className="pb-2 mb-2 flex-shrink-0 flex flex-wrap gap-3">
              {/* First Column - Search and Legend Card */}
              <div
                className="w-full correlation-control-item"
                style={{ minWidth: 0 }}
              >
                <Card
                  className="h-full"
                  style={{ border: 'none' }}
                  pt={{
                    body: { className: 'p-2 h-full' },
                    content: { className: 'p-0 m-0' },
                  }}
                >
                  <div className="flex flex-column gap-2">
                    <div className="flex flex-column gap-1">
                      <label
                        className="text-xs font-semibold"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Search Company
                      </label>
                      <InputText
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Type ticker or company name..."
                        className="w-full"
                      />
                      {selectedTicker && (
                        <div
                          className="text-xs mt-1"
                          style={{ color: 'var(--bq-text-secondary)' }}
                        >
                          Selected:{' '}
                          <strong
                            style={{
                              color: 'var(--bq-color-info-strong, #3b82f6)',
                            }}
                          >
                            {selectedTicker}
                          </strong>
                          {' - '}
                          {
                            data.nodes.find((n) => n.ticker === selectedTicker)
                              ?.company_name
                          }
                        </div>
                      )}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-column gap-2 pt-2">
                      <div className="flex align-items-center gap-2">
                        <span
                          className="text-xs"
                          style={{ color: 'var(--bq-text-secondary)' }}
                        >
                          <strong>Circle Size:</strong>
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: 'var(--bq-text-primary)' }}
                        >
                          Allocation %
                        </span>
                      </div>
                      <div className="flex align-items-center gap-2">
                        <span
                          className="text-xs"
                          style={{ color: 'var(--bq-text-secondary)' }}
                        >
                          <strong>Circle Color:</strong>
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: 'var(--bq-text-primary)' }}
                        >
                          {metricType === 'drawdown'
                            ? 'Drawdown Risk'
                            : 'Beta (Market Sensitivity)'}
                        </span>
                      </div>
                      <div className="flex align-items-center gap-2">
                        <span
                          className="text-xs"
                          style={{ color: 'var(--bq-text-secondary)' }}
                        >
                          <strong>Circle Position:</strong>
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: 'var(--bq-text-primary)' }}
                        >
                          Correlation
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Second Column - Button Controls Card */}
              <div
                className="w-full correlation-control-item"
                style={{ minWidth: 0 }}
              >
                <Card
                  className="h-full"
                  style={{ border: 'none' }}
                  pt={{
                    body: { className: 'p-2 h-full' },
                    content: { className: 'p-0 m-0' },
                  }}
                >
                  <div className="flex flex-column gap-2">
                    {/* Portfolio Type Toggle */}
                    <div className="flex flex-column gap-1">
                      <label
                        className="text-xs font-semibold"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Portfolio Type
                      </label>
                      <SelectButton
                        value={portfolioType}
                        onChange={(e) => {
                          // Prevent deselection - only allow changing to the other option
                          if (e.value && e.value !== portfolioType) {
                            setPortfolioType(e.value as 'current' | 'proposed');
                          }
                        }}
                        options={[
                          { label: 'Current', value: 'current' },
                          { label: 'Proposed', value: 'proposed' },
                        ]}
                      />
                    </div>

                    {/* Metric Type Toggle */}
                    <div className="flex flex-column gap-1">
                      <label
                        className="text-xs font-semibold"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Color By
                      </label>
                      <SelectButton
                        value={metricType}
                        onChange={(e) => {
                          // Prevent deselection - only allow changing to the other option
                          if (e.value && e.value !== metricType) {
                            setMetricType(e.value as 'drawdown' | 'beta');
                          }
                        }}
                        options={[
                          { label: 'Drawdown', value: 'drawdown' },
                          { label: 'Beta', value: 'beta' },
                        ]}
                      />
                    </div>
                  </div>
                </Card>
              </div>

              {/* Third Column - Stats Summary Card */}
              <div
                className="w-full correlation-control-item"
                style={{ minWidth: 0 }}
              >
                <Card
                  className="h-full"
                  style={{ border: 'none' }}
                  pt={{
                    body: { className: 'p-2 h-full' },
                    content: { className: 'p-0 m-0' },
                  }}
                >
                  <div className="flex flex-column gap-2">
                    <div className="flex align-items-center gap-2">
                      <span
                        className="text-xs"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Total Assets:
                      </span>
                      <span
                        className="text-sm font-bold"
                        style={{ color: 'var(--bq-text-primary)' }}
                      >
                        {data.metadata.total_assets}
                      </span>
                    </div>

                    {metricType === 'drawdown' ? (
                      <>
                        <div className="flex align-items-center gap-2">
                          <span
                            className="text-xs"
                            style={{ color: 'var(--bq-text-secondary)' }}
                          >
                            Max Drawdown:
                          </span>
                          <span
                            className="text-sm font-bold"
                            style={{ color: 'var(--bq-color-negative)' }}
                          >
                            {Number(data.metadata.min_drawdown).toFixed(2)}%
                          </span>
                        </div>
                        <div className="flex align-items-center gap-2">
                          <span
                            className="text-xs"
                            style={{ color: 'var(--bq-text-secondary)' }}
                          >
                            Best Drawdown:
                          </span>
                          <span
                            className="text-sm font-bold"
                            style={{ color: 'var(--bq-color-positive)' }}
                          >
                            {Number(data.metadata.max_drawdown).toFixed(2)}%
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex align-items-center gap-2">
                          <span
                            className="text-xs"
                            style={{ color: 'var(--bq-text-secondary)' }}
                          >
                            Min Beta:
                          </span>
                          <span
                            className="text-sm font-bold"
                            style={{ color: 'var(--bq-color-positive)' }}
                          >
                            {minBeta != null ? minBeta.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        <div className="flex align-items-center gap-2">
                          <span
                            className="text-xs"
                            style={{ color: 'var(--bq-text-secondary)' }}
                          >
                            Max Beta:
                          </span>
                          <span
                            className="text-sm font-bold"
                            style={{ color: 'var(--bq-color-negative)' }}
                          >
                            {maxBeta != null ? maxBeta.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                      </>
                    )}

                    <div className="flex align-items-center gap-2">
                      <span
                        className="text-xs"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Lookback Period:
                      </span>
                      <span
                        className="text-sm font-bold"
                        style={{ color: 'var(--bq-text-primary)' }}
                      >
                        {data.lookback_days}d
                      </span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Fourth Column - Correlation Metrics Card */}
              <div
                className="w-full correlation-control-item"
                style={{ minWidth: 0 }}
              >
                <Card
                  className="h-full"
                  style={{ border: 'none' }}
                  pt={{
                    body: { className: 'p-2 h-full' },
                    content: { className: 'p-0 m-0' },
                  }}
                >
                  <span
                    className="text-xs font-semibold mb-2 block"
                    style={{ color: 'var(--bq-text-secondary)' }}
                  >
                    Correlation Metrics
                  </span>
                  <div className="flex flex-column gap-2">
                    <div className="flex align-items-center gap-2">
                      <span
                        className="text-xs"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Avg:
                      </span>
                      <span
                        className="text-sm font-bold"
                        style={{ color: 'var(--bq-text-primary)' }}
                      >
                        {correlationStats.avgCorrelation != null
                          ? correlationStats.avgCorrelation.toFixed(2)
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <span
                        className="text-xs"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Max:
                      </span>
                      <span
                        className="text-sm font-bold"
                        style={{
                          color:
                            correlationStats.maxCorrelation != null &&
                            correlationStats.maxCorrelation >= 0.8
                              ? 'var(--bq-color-negative)'
                              : 'var(--bq-text-primary)',
                        }}
                      >
                        {correlationStats.maxCorrelation != null
                          ? correlationStats.maxCorrelation.toFixed(2)
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <span
                        className="text-xs"
                        style={{ color: 'var(--bq-text-secondary)' }}
                      >
                        Min:
                      </span>
                      <span
                        className="text-sm font-bold"
                        style={{
                          color:
                            correlationStats.minCorrelation != null &&
                            correlationStats.minCorrelation < 0
                              ? 'var(--bq-color-positive)'
                              : 'var(--bq-text-primary)',
                        }}
                      >
                        {correlationStats.minCorrelation != null
                          ? correlationStats.minCorrelation.toFixed(2)
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Fifth Column - Top Correlated Pairs Card */}
              <div
                className="w-full correlation-control-item-full"
                style={{ minWidth: 0 }}
              >
                <Card
                  className="h-full"
                  style={{ border: 'none' }}
                  pt={{
                    body: { className: 'p-2 h-full flex flex-column' },
                    content: { className: 'p-0 m-0 flex-1 overflow-hidden' },
                  }}
                >
                  <span
                    className="text-xs font-semibold mb-2 block flex-shrink-0"
                    style={{ color: 'var(--bq-text-secondary)' }}
                  >
                    Top Correlations
                  </span>
                  <div
                    className="flex flex-column gap-2 overflow-y-auto"
                    style={{ maxHeight: '120px' }}
                  >
                    {filteredTopCorrelatedPairs.length > 0 ? (
                      filteredTopCorrelatedPairs.map((pair, index) => {
                        // Try correlationTickerNames first, then fall back to tickerToCompanyName
                        const lookupCompany = (ticker: string) =>
                          correlationTickerNames?.get(ticker) ||
                          correlationTickerNames?.get(ticker.toUpperCase()) ||
                          correlationTickerNames?.get(ticker.split('.')[0]) ||
                          tickerToCompanyName.get(ticker) ||
                          tickerToCompanyName.get(ticker.toUpperCase()) ||
                          tickerToCompanyName.get(ticker.split('.')[0]);

                        const company1 = lookupCompany(pair.asset1);
                        const company2 = lookupCompany(pair.asset2);

                        return (
                          <div
                            key={`${pair.asset1}-${pair.asset2}-${index}`}
                            className="flex justify-content-between align-items-center gap-2 flex-shrink-0"
                          >
                            <span
                              className="text-xs"
                              style={{ color: 'var(--bq-text-secondary)' }}
                            >
                              {company1 ? (
                                <>
                                  <Tooltip
                                    target={`.ticker-${index}-1`}
                                    content={company1}
                                    position="top"
                                  />
                                  <span
                                    className={`ticker-${index}-1 cursor-pointer`}
                                    style={{
                                      borderBottom:
                                        '1px dotted var(--bq-text-tertiary)',
                                    }}
                                  >
                                    {pair.asset1}
                                  </span>
                                </>
                              ) : (
                                pair.asset1
                              )}
                              {' - '}
                              {company2 ? (
                                <>
                                  <Tooltip
                                    target={`.ticker-${index}-2`}
                                    content={company2}
                                    position="top"
                                  />
                                  <span
                                    className={`ticker-${index}-2 cursor-pointer`}
                                    style={{
                                      borderBottom:
                                        '1px dotted var(--bq-text-tertiary)',
                                    }}
                                  >
                                    {pair.asset2}
                                  </span>
                                </>
                              ) : (
                                pair.asset2
                              )}
                            </span>
                            <span
                              className="text-sm font-bold"
                              style={{
                                color: pair.isPositive
                                  ? 'var(--bq-text-primary)'
                                  : 'var(--bq-color-negative)',
                              }}
                            >
                              {pair.correlation >= 0 ? '+' : ''}
                              {pair.correlation.toFixed(2)}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <span
                        className="text-xs"
                        style={{ color: 'var(--bq-text-tertiary)' }}
                      >
                        No data
                      </span>
                    )}
                  </div>
                </Card>
              </div>
            </div>

            {/* Divider */}
            <div
              className="border-bottom-1 mb-2"
              style={{ borderBottom: '1px solid var(--bq-border-muted)' }}
            ></div>

            {/* Chart */}
            <div
              className="flex-1 min-h-0"
              style={{ minHeight: '300px', position: 'relative' }}
            >
              <CorrelationClusterChart
                key={`${widgetId}-${metricType}`}
                data={data}
                metricType={metricType}
                selectedTicker={selectedTicker}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
